// Analytics tab › Schema Design (spec §7): master-detail — Visual Schemas
// (M, shared data models) on the left, the EXISTING embedded
// SchemaDesignerPage on the right. "New" generates a draft schema with AI.
import { useCallback, useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import SchemaDesignerPage from '@/pages/designers/SchemaDesignerPage'
import {
  generateVisualSchema,
  saveVisualSchema,
  deleteVisualSchema,
  listVisualSchemas,
  type VisualSchemaSummary,
} from '@/api/designers'
import ObjectMasterDetail, { type MasterItem } from './ObjectMasterDetail'
import { EmptyStateCoach, TypeChip, errMessage, slugify } from './shared'

export default function SchemaSection({
  project, registerLeaveGuard, initialObjName, openNonce, onChanged,
}: {
  project: string
  registerLeaveGuard?: (fn: ((proceed: () => void) => void) | null) => void
  initialObjName?: string
  openNonce?: number
  onChanged?: () => void
}) {
  const [schemas, setSchemas] = useState<VisualSchemaSummary[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const refresh = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await listVisualSchemas(project)
      setSchemas(res.schemas || [])
    } catch (err) {
      setError(errMessage(err, 'Could not load schema designs'))
    } finally {
      setLoading(false)
    }
  }, [project])

  useEffect(() => { void refresh() }, [refresh])

  const items: MasterItem[] = schemas.map((s) => ({
    id: s.schema_name,
    name: s.schema_name,
    subtitle: `${s.schema_name} · visual schema`,
    description: s.schema_descr || undefined,
    tags: [`${s.table_count} table${s.table_count === 1 ? '' : 's'}`],
  }))

  const handleCreate = async (name: string, description: string): Promise<string | null> => {
    const schemaName = slugify(name)
    try {
      const res = await generateVisualSchema({
        project,
        schema_name: schemaName,
        description: description || name,
      })
      if (res.error) {
        toast.error(res.error)
        return null
      }
      const failed = res.verification?.failures?.length || 0
      if (failed > 0) {
        toast.error(
          `Schema "${schemaName}" drafted but ${failed} consuming visual${failed === 1 ? '' : 's'} still fail${failed === 1 ? 's' : ''} — open it and check the joins/columns`,
          { duration: 6000 },
        )
      } else {
        toast.success(`Schema design "${schemaName}" drafted — review and save it`)
      }
      await refresh()
      onChanged?.()
      return schemaName
    } catch (err) {
      toast.error(errMessage(err, 'Could not generate the schema design'))
      return null
    }
  }

  // Manual path: persist a blank schema (no AI) and open the designer so the
  // user imports tables, builds joins and folders themselves.
  const handleCreateManual = async (name: string, description: string): Promise<string | null> => {
    const schemaName = slugify(name)
    try {
      const file = {
        SCHEMA: {
          SCHEMA_NAME: schemaName,
          SCHEMA_DESCR: description || schemaName.replace(/_/g, ' '),
          SCHEMA_TYPE: 'S', CONNECTION_ID: '', MAIN_TABLE: '',
          TABLES: [], JOINS: [], ROW_SECURITY: [], COLUMN_SECURITY: [],
          COLUMN_GROUPS: [],
        },
      }
      await saveVisualSchema(project, schemaName, file)
      toast.success(`Schema design "${schemaName}" created — import tables and build joins`)
      await refresh()
      onChanged?.()
      return schemaName
    } catch (err) {
      toast.error(errMessage(err, 'Could not create the schema design'))
      return null
    }
  }

  return (
    <ObjectMasterDetail
      project={project}
      typeCode="M"
      heading="Schema Designs"
      subCopy="A schema design turns raw source tables into a business model: logical folders of columns, joins, and security. Visuals and business areas build on it."
      items={items}
      loading={loading}
      error={error}
      onRetry={() => void refresh()}
      newLabel="New schema design"
      newDialogTitle="New schema design"
      namePlaceholder="e.g. Pharma Sales Schema"
      descPlaceholder="e.g. Sales & distribution analytics. Fact grain: one row per invoice line, with product, customer and date dimensions."
      createButtonLabel="Generate with AI"
      onCreate={handleCreate}
      onCreateManual={handleCreateManual}
      manualButtonLabel="Create manually"
      onObjectSaved={() => { void refresh(); onChanged?.() }}
      registerLeaveGuard={registerLeaveGuard}
      initialObjName={initialObjName}
      openNonce={openNonce}
      deleteConfig={{
        objType: 'M',
        objLabel: 'schema design',
        doDelete: async (id) => { await deleteVisualSchema(project, id) },
        onDeleted: () => { void refresh(); onChanged?.() },
      }}
      emptyState={(openNew) => (
        <EmptyStateCoach
          icon={<TypeChip code="M" size={26} />}
          title="No schema designs yet"
          body="A Data Model (schema design) decides which tables and joins your charts can use. Create one from a plain-language description — or link a data source first under Connections."
          action={
            <button className="btn btn-primary btn-sm" onClick={openNew}>
              + New schema design
            </button>
          }
        />
      )}
      renderDesigner={(objName, common) => (
        <SchemaDesignerPage key={`${project}:${objName}`} {...common} />
      )}
    />
  )
}
